import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { CheckCircle2, Circle, Flame, ChevronRight } from "lucide-react";
import { getHabitsToday, logHabit } from "@/lib/api";

export default function HabitWidget() {
  const [checklist, setChecklist] = useState([]);
  const [completed, setCompleted] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadToday();
  }, []);

  const loadToday = async () => {
    try {
      const res = await getHabitsToday();
      setChecklist(res.data.checklist || []);
      setCompleted(res.data.completed || 0);
      setTotal(res.data.total || 0);
    } catch {}
    setLoading(false);
  };

  const handleToggle = async (habitId) => {
    setChecklist(prev => prev.map(h =>
      h.habit_id === habitId ? { ...h, completed: !h.completed } : h
    ));
    setCompleted(prev => {
      const habit = checklist.find(h => h.habit_id === habitId);
      return habit?.completed ? prev - 1 : prev + 1;
    });

    try {
      const res = await logHabit(habitId);
      setChecklist(prev => prev.map(h =>
        h.habit_id === habitId ? { ...h, streak: res.data.streak, completed: res.data.completed } : h
      ));
    } catch {
      loadToday();
    }
  };

  if (loading) return null;
  if (total === 0) {
    return (
      <Card className="border-[#E5E0D5] bg-white" data-testid="habit-widget">
        <CardContent className="p-3">
          <Link to="/my-rule-of-life" className="flex items-center justify-between group">
            <div>
              <p className="font-['Crimson_Text'] text-sm font-semibold text-[#2D2A26]">Rule of Life</p>
              <p className="font-['Nunito'] text-xs text-[#6B665E]">Start tracking your spiritual habits</p>
            </div>
            <ChevronRight className="w-4 h-4 text-[#6B665E] group-hover:text-[var(--liturgical-primary)] transition-colors" />
          </Link>
        </CardContent>
      </Card>
    );
  }

  const pct = Math.round((completed / total) * 100);

  return (
    <Card className="border-[#E5E0D5] bg-white" data-testid="habit-widget">
      <CardContent className="p-3">
        {/* Header */}
        <Link to="/my-rule-of-life" className="flex items-center justify-between mb-2 group">
          <p className="font-['Crimson_Text'] text-sm font-semibold text-[#2D2A26]">Rule of Life</p>
          <div className="flex items-center gap-1">
            <span className="font-['Nunito'] text-xs font-medium" style={{ color: "var(--liturgical-primary)" }}>
              {completed}/{total}
            </span>
            <ChevronRight className="w-3.5 h-3.5 text-[#6B665E] group-hover:text-[var(--liturgical-primary)] transition-colors" />
          </div>
        </Link>

        {/* Mini progress bar */}
        <div className="w-full h-1.5 rounded-full bg-[#F0EBE0] mb-2.5">
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{ width: `${pct}%`, backgroundColor: "var(--liturgical-primary)" }}
          />
        </div>

        {/* Compact checklist (max 5 visible, only due today) */}
        <div className="space-y-1">
          {checklist.filter(h => h.due_today !== false).slice(0, 5).map((h) => (
            <button
              key={h.habit_id}
              onClick={() => handleToggle(h.habit_id)}
              className="w-full flex items-center gap-2 py-1 px-1 rounded-md hover:bg-[#F9F7F2] transition-colors"
              data-testid={`widget-habit-${h.habit_id}`}
            >
              {h.completed ? (
                <CheckCircle2 className="w-4 h-4 flex-shrink-0" style={{ color: "var(--liturgical-primary)" }} />
              ) : (
                <Circle className="w-4 h-4 text-[#D5D0C8] flex-shrink-0" />
              )}
              <span className={`font-['Nunito'] text-xs flex-1 text-left truncate ${
                h.completed ? "text-[#6B665E] line-through" : "text-[#2D2A26]"
              }`}>
                {h.name}
              </span>
              {h.streak > 0 && (
                <span className="flex items-center text-[10px] text-orange-500 font-['Nunito']">
                  <Flame className="w-2.5 h-2.5" />{h.streak}
                </span>
              )}
            </button>
          ))}
          {checklist.filter(h => h.due_today !== false).length > 5 && (
            <Link to="/my-rule-of-life" className="block text-center">
              <span className="font-['Nunito'] text-[10px] text-[var(--liturgical-primary)] hover:underline">
                +{checklist.filter(h => h.due_today !== false).length - 5} more
              </span>
            </Link>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
